import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { LinkTabNavHorizontal } from '../../components';

import css from './UserNav.module.css';
import { getUserTypePreference } from '../../util/dataExtractor.js';
import { UserTypePreference } from '../../util/enums';
import { createSlug, LISTING_PAGE_PARAM_TYPE_DRAFT, LISTING_PAGE_PARAM_TYPE_EDIT } from '../../util/urlHelpers';

const UserNav = props => {
  const { className, rootClassName, currentPage, showManageListingsLink } = props;
  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  const { currentUser } = useSelector(state => state.user);
  const userType = getUserTypePreference(currentUser);
  const isSeller = userType === UserTypePreference.PRIVATE_CHEFS;

  const authorListingId = currentUser?.attributes?.profile?.publicData?.authorListingId || "";
  const isPublished = currentUser?.attributes?.profile?.publicData?.isPublished;
  const title = currentUser?.attributes?.profile?.displayName;
  const slug = title && createSlug(title);

  // "Your listings" only for sellers
  const manageListingsTabMaybe =
    isSeller && showManageListingsLink
      ? [
          {
            text: <FormattedMessage id="UserNav.yourListings" />,
            selected: currentPage === 'ManageListingsPage',
            linkProps: {
              name: 'ManageListingsPage',
            },
          },
        ]
      : [];

  // Profile tab:
  // - Sellers with authorListingId → EditListingPage (chef profile listing)
  // - Everyone else → ProfileSettingsPage (default behaviour)
  const hasAuthorListing = isSeller && authorListingId;

  const profileTab = hasAuthorListing
    ? {
        text: <FormattedMessage id="UserNav.profileSettings" />,
        // if you ever mount this nav on EditListingPage, you can add that check too
        selected: currentPage === 'EditListingPage',
        disabled: false,
        linkProps: {
          name: 'EditListingPage',
          params: {
            id: authorListingId,
            slug,
            type: isPublished ? LISTING_PAGE_PARAM_TYPE_EDIT : LISTING_PAGE_PARAM_TYPE_DRAFT,
            tab: 'basic-info',
          },
        },
      }
    : {
        text: <FormattedMessage id="UserNav.profileSettings" />,
        selected: currentPage === 'ProfileSettingsPage',
        disabled: false,
        linkProps: {
          name: 'ProfileSettingsPage',
        },
      };

  const tabs = [
    ...manageListingsTabMaybe,
    profileTab,
    {
      text: <FormattedMessage id="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
  ];

  return (
    <LinkTabNavHorizontal
      className={classes}
      tabRootClassName={css.tab}
      tabs={tabs}
      skin="dark"
      ariaLabel={intl.formatMessage({ id: 'UserNav.screenreader.userNav' })}
    />
  );
};

export default UserNav;
